import React, { useState } from 'react';
import { Check, ShieldCheck, User, FileCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../lib/api';
import { openInquiryPopup } from '../lib/contact';

const services = [
  'Guidance on student visa\nrequirements',
  'Assistance in preparing\nall necessary documents',
  'Invitation letter processing\nfrom the university',
  'Visa application\nform filling',
  'Appointment scheduling\nat the Russian\nConsulate/Visa Center',
  'Medical tests and\ninsurance guidance',
  'Travel date planning\naccording\nto visa validity',
];

const carefulChecks = [
  'Passport validity',
  'Admission and invitation',
  'Medical reports',
  'Financial documents',
  'Visa application forms',
];

const alsoProvides = [
  'Pre-visa counseling\nsessions',
  'Step-by-step guidance\nfor first-time applicants',
  'Updates on visa status',
  'Support in case of\nadditional Consular\nrequirements',
];

function NumBadge({ n }) {
  return (
    <div className="absolute -top-3 right-4 w-8 h-8 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center shadow-lg shadow-brand/40">{n}</div>
  );
}

export default function VisaSupport() {
  const { toast } = useToast();
  const [q, setQ] = useState({ name: '', phone: '', email: '', consent: false });
  const [f, setF] = useState({ name: '', phone: '', email: '', msg: '', consent: false });

  const [submittingQuick, setSubmittingQuick] = useState(false);
  const [submittingFull, setSubmittingFull] = useState(false);

  const submitQuick = async (e) => {
    e.preventDefault();
    if (!q.name || !q.phone || !q.email) { toast({ title: 'Fill Name, Phone & Email', variant: 'destructive' }); return; }
    if (!q.consent) { toast({ title: 'Please accept privacy policy', variant: 'destructive' }); return; }
    setSubmittingQuick(true);
    try {
      await submitInquiry({ type: 'visa_quick', name: q.name, phone: q.phone, email: q.email, consent: q.consent });
      toast({ title: 'Request Sent!', description: `Thanks ${q.name} — our visa team will call you shortly.` });
      setQ({ name: '', phone: '', email: '', consent: false });
    } catch (err) {
      toast({ title: 'Could not send', description: err.message, variant: 'destructive' });
    } finally {
      setSubmittingQuick(false);
    }
  };

  const submitFull = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email) { toast({ title: 'Fill Name, Phone & Email', variant: 'destructive' }); return; }
    if (!f.consent) { toast({ title: 'Please accept privacy policy', variant: 'destructive' }); return; }
    setSubmittingFull(true);
    try {
      await submitInquiry({ type: 'visa_full', name: f.name, phone: f.phone, email: f.email, message: f.msg, consent: f.consent });
      toast({ title: 'Message Sent!', description: `Thank you ${f.name} — we will respond within 24 hours.` });
      setF({ name: '', phone: '', email: '', msg: '', consent: false });
    } catch (err) {
      toast({ title: 'Could not send', description: err.message, variant: 'destructive' });
    } finally {
      setSubmittingFull(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="text-white">
            <div className="flex items-start gap-6 mb-6">
              <img src="/logo.jpg" alt="Russian Education" className="w-16 h-16 rounded-full border-2 border-white/40 bg-white p-1" />
              <div>
                <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">your one stop gateway<br />to Russian universities</div>
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">VISA SUPPORT</h1>
            <Button onClick={openInquiryPopup} data-testid="visa-apply-hero" className="mt-8 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift">Apply now</Button>
          </div>
          <div className="hidden md:block">
            <img src="https://images.unsplash.com/photo-1541752750385-9d1180525b34?w=500&q=80" alt="Student" className="w-80 h-80 lg:w-96 lg:h-96 object-cover rounded-2xl shadow-2xl border-4 border-white/20" />
          </div>
        </div>
      </section>

      {/* Main statement */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight max-w-4xl">
            Getting a student visa to Russia is quite easy. Unlike many other countries, you do not need a lot of documents. Visa success ratio is about 99%, process will take 2 weeks
          </h2>
        </div>
      </section>

      {/* Two column */}
      <section className="pb-14">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-border p-7 shadow-sm">
            <p className="text-base md:text-lg text-foreground leading-relaxed font-medium">
              At the Russian Education, we provide complete and reliable visa support to make the process smooth, fast, and stress-free for you and your parents
            </p>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
              Our experienced team guides you through every stage of the visa application, ensuring that all documents are accurate and submitted on time. With our professional assistance, your chances of visa approval are significantly higher.
            </p>
            <div className="mt-5 flex gap-2">
              <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center"><User className="w-4 h-4" /></div>
              <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden bg-primary min-h-[320px] flex items-center justify-center p-8">
            <div className="text-9xl">🛂</div>
          </div>
        </div>
      </section>

      {/* Our Visa Support Services Include */}
      <section className="py-14 md:py-16 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-10">Our Visa Support Services Include:</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {services.slice(0, 3).map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <NumBadge n={i + 1} />
                <p className="font-serif text-base md:text-lg font-semibold text-primary whitespace-pre-line leading-snug">{s}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 grid md:grid-cols-4 gap-4">
            {services.slice(3).map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <NumBadge n={i + 4} />
                <p className="font-serif text-sm md:text-base font-semibold text-primary whitespace-pre-line leading-snug">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo + We carefully check */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl overflow-hidden bg-muted min-h-[320px]">
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&q=80" alt="Student" className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary mb-6">We carefully check:</h3>
            <div className="grid grid-cols-2 gap-3">
              {carefulChecks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2.5">
                  <div className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" strokeWidth={3} /></div>
                  <span className="text-sm text-foreground font-medium">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Inline Contact banner */}
      <section className="pb-10">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="bg-primary text-white rounded-2xl p-6 md:p-8 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5zm0 30c-14 8-26 25-26 42 0 12 12 24 26 24s26-12 26-24c0-17-12-34-26-42z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />
            <div className="relative">
              <h3 className="font-serif text-2xl md:text-3xl font-bold">Contact us</h3>
              <p className="text-xs md:text-sm text-white/85 mt-1">Send a request to get a consultation.</p>
              <form onSubmit={submitQuick} className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
                <input value={q.name} onChange={(e) => setQ({ ...q, name: e.target.value })} placeholder="Name" className="h-10 px-3 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={q.phone} onChange={(e) => setQ({ ...q, phone: e.target.value })} type="tel" placeholder="Telephone" className="h-10 px-3 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={q.email} onChange={(e) => setQ({ ...q, email: e.target.value })} type="email" placeholder="Email" className="h-10 px-3 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                <Button type="submit" className="bg-brand hover:bg-brand-dark text-white h-10 rounded-lg font-bold">Apply now</Button>
              </form>
              <label className="mt-3 flex items-start gap-2 text-[11px] text-white/80 cursor-pointer">
                <input type="checkbox" checked={q.consent} onChange={(e) => setQ({ ...q, consent: e.target.checked })} className="mt-0.5 accent-brand" />
                <span>I consent to the processing of personal data and receiving newsletters from the Russian Education, I agree with the privacy policy</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* Our team also provides */}
      <section className="py-14 md:py-16 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-10">Our team also provides:</h3>
          <div className="grid md:grid-cols-4 gap-4">
            {alsoProvides.map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <NumBadge n={i + 1} />
                <p className="font-serif text-base font-semibold text-primary whitespace-pre-line leading-snug">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Group photo + closing statement */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl overflow-hidden bg-muted min-h-[320px]">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=900&q=80" alt="Students" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="bg-white rounded-2xl border border-border p-7 shadow-sm">
              <p className="font-serif text-xl md:text-2xl font-bold text-primary leading-tight">
                With the Russian Education, you can be confident that your visa process is handled professionally and transparently
              </p>
              <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
                We aim to make your travel from India to Russia simple and secure, so you can focus fully on your education in MBBS or Engineering.
              </p>
              <div className="mt-5 flex gap-2">
                <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center"><User className="w-4 h-4" /></div>
                <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center"><FileCheck className="w-4 h-4" /></div>
              </div>
              <Button onClick={openInquiryPopup} data-testid="visa-apply-side" className="mt-5 w-full bg-brand hover:bg-brand-dark text-white h-11 rounded-lg font-bold">Apply now</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom tagline strip */}
      <section className="pb-14">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-4">
          <div className="text-lg md:text-xl font-serif font-bold text-primary">
            Your journey to a successful international career starts with the right visa support —
          </div>
          <div className="bg-primary text-white rounded-xl p-5">
            <p className="text-base font-semibold">and we are here to guide you every step of the way!</p>
          </div>
        </div>
      </section>

      {/* Contact section (blue + red) */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-primary text-white rounded-2xl p-7 md:p-9">
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-3">Contact us</h3>
            <p className="text-sm text-white/85 leading-relaxed mb-6">We are always here to help you take the first step toward your international education in Russia.</p>
            <form onSubmit={submitFull} className="space-y-3">
              <textarea value={f.msg} onChange={(e) => setF({ ...f, msg: e.target.value })} placeholder="Send a message" rows={3} className="w-full px-3 py-2.5 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
              <div className="grid grid-cols-3 gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className="h-10 px-3 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} type="tel" placeholder="Telephone" className="h-10 px-3 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" placeholder="Email" className="h-10 px-3 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
              <Button type="submit" className="w-full bg-black hover:bg-black/80 text-white h-11 rounded-lg font-bold">Apply now</Button>
              <label className="flex items-start gap-2 text-xs text-white/80 pt-2 cursor-pointer">
                <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 accent-brand" />
                <span>I consent to the processing of personal data and receiving newsletters from Russian Education, I agree with the privacy policy</span>
              </label>
            </form>
          </div>

          <div className="bg-brand text-white rounded-2xl p-7 md:p-9">
            <div className="space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Email:</div>
                <a href="mailto:admissions@russianeducation.in" className="mt-1 block text-lg font-semibold hover:underline">admissions@russianeducation.in</a>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Phone:</div>
                  <a href="tel:+919509406802" className="mt-1 block text-sm font-bold hover:underline">+91 95094 06802</a>
                  <div className="mt-2 text-xs text-white/85">Also on WhatsApp</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Working Hours:</div>
                  <div className="mt-1 text-xs">Monday to Friday: 10:00 AM – 7:00 PM</div>
                  <div className="mt-1 text-xs">Saturday: By appointment only</div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Head Office:</div>
                <div className="mt-1 text-sm font-semibold">Russian Education</div>
                <div className="text-xs text-white/85 leading-relaxed">Alex Hospital, Shiv Gate,<br />Sector 115, Noida</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-white/75 font-semibold">Corporate Office:</div>
                <div className="mt-1 text-xs text-white/85 leading-relaxed">God Gift Complex, Hiran Magri,<br />Sector 3, Udaipur</div>
              </div>
              <div className="rounded-lg overflow-hidden border border-white/20">
                <iframe title="office-map" src="https://maps.google.com/maps?q=Sector%20115%20Noida&t=&z=13&ie=UTF8&iwloc=&output=embed" width="100%" height="180" style={{ border: 0 }} loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="py-4 bg-white border-t border-border">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© 1988 — 2026, Russian Education</div>
          <div>All rights reserved</div>
        </div>
      </div>
    </div>
  );
}
